// EmailVerifyBanner — persistent strip shown when the user is signed
// in but hasn't clicked the confirmation link yet. Includes a
// "Skicka igen"-button with a 60 s client-side cooldown.
//
// Renders nothing when verified / signed out / dismissed this session.

import { useState } from 'react';
import { MailWarning, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const RESEND_COOLDOWN_MS = 60_000;

export function EmailVerifyBanner() {
  const { user, isAuthenticated, isEmailVerified } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSentAt, setLastSentAt] = useState(0);

  if (!isAuthenticated || isEmailVerified || dismissed || !user?.email) return null;

  const cooldownLeft = Math.max(0, RESEND_COOLDOWN_MS - (Date.now() - lastSentAt));

  const handleResend = async () => {
    if (cooldownLeft > 0) return;
    setSending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email!,
      });
      if (error) throw error;
      setLastSentAt(Date.now());
      toast({ title: 'Bekräftelsemejl skickat', description: 'Kolla inkorgen (och skräpposten).' });
    } catch (e) {
      toast({
        title: 'Kunde inte skicka',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="status"
      data-testid="email-verify-banner"
      className="flex items-center gap-3 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-300/50 text-xs"
    >
      <MailWarning className="w-4 h-4 text-amber-600 flex-shrink-0" aria-hidden="true" />
      <span className="flex-1">
        Bekräfta din e-postadress — vi har skickat en länk till{' '}
        <strong>{user.email}</strong>. Vissa funktioner (co-op, delning) är låsta tills dess.
      </span>
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={handleResend}
        disabled={sending || cooldownLeft > 0}
        data-testid="email-verify-resend"
      >
        {sending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
        {cooldownLeft > 0 ? `Vänta ${Math.ceil(cooldownLeft / 1000)}s` : 'Skicka igen'}
      </Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-muted-foreground hover:text-foreground"
        aria-label="Stäng bannern"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
